// Catalog.ID Client — Invitation Reference Card
// ============================================================================
// The printed distribution channel for an invitation (registration-restructure
// §13.5, amended by §14.2, generalised by §14.3). The invitation itself lives on
// the node — a delegate invitation's hybrid signature is 3.3 kB and fits in no
// QR (§11) — so the card carries only a REFERENCE: a 16-character CODE.
//
// THREE KINDS since §14.3, and only the details table differs between them: a
// DELEGATE card names the organization and the roles it grants; a PERSONAL or
// ORGANIZATION card names neither, because there is no organization behind it —
// somebody bought a registration and addressed it to one person. `kind` picks
// the title and the rows; it defaults to `delegate`, so every existing caller
// and every sheet already printed is unchanged.
//
// The code is printed twice, and that is the whole design (§14.2). In large
// type, because an invitee sitting at a printer has to be able to TYPE it, read
// it back over a telephone, or copy it out of a letter; and inside a QR, for
// laptop cameras and Wi-Fi printing from a phone. The QR carries the entry URL
// with the code in its fragment and NOTHING ELSE — in particular not the fixed
// email or phone, so one invitation behaves the same way however the invitee
// arrived. Those are printed here, in ink, and typed by the invitee.
//
// Physical form: credit-card 85.6 × 54 mm, ten to an A4 sheet in a 2 × 5 grid,
// cut apart along light crop marks. Two consequences shape this module:
//
//  - NO letterhead. The sheet is cut into ten pieces; stationery artwork would
//    be sliced along with it. The sheet therefore brings its OWN background
//    (the crop marks), which is also what stops `withLetterhead` in
//    `pdf-loader.ts` from filling one in on the way to the printer.
//  - NO named styles. The card content is embedded in two documents here and
//    may be embedded in a third; a style name its host forgot to declare would
//    silently fall back to 10 pt and push the URL off the trim box. Every text
//    node carries its own explicit `fontSize`.
//
// Like `booklet-definition.ts`, this module is ASSET-FREE: no bundler-only
// `?raw`/`?url` import may appear anywhere in what it pulls in, not even behind
// a dynamic `import()`, or the sample renderer (`apps/cid/dev/render-booklet.ts`
// — plain tsx, no Vite, and typechecked by `apps/cid/dev/tsconfig.json`) can
// neither run nor typecheck it. `pdf-loader.ts` reaches the letterhead SVG that
// way and pdf.js its worker, so the PNG export lives in its own browser-only
// module, `invitation-card-png.ts`, which nothing here imports.
//
// All ink is black — a reference card gets photocopied, faxed and printed on
// whatever is in the office.

import type { Content, TableCell, TableLayout, TDocumentDefinitions } from 'pdfmake/interfaces';
import { qrToSvg, type QrSvgOptions } from '../qr-svg';
import { encodeUtf8 } from '../../crypto/aes';

// ── geometry (all points; 1 mm = 72/25.4 pt) ────────────────────────────────

/** ISO/IEC 7810 ID-1 ("credit card"): 85.6 mm. */
export const CARD_WIDTH_PT = 242.6;
/** ISO/IEC 7810 ID-1: 54 mm. */
export const CARD_HEIGHT_PT = 153.1;

export const CARD_COLUMNS = 2;
export const CARD_ROWS = 5;
export const CARDS_PER_SHEET = CARD_COLUMNS * CARD_ROWS;

const A4_WIDTH_PT = 595.28;
const A4_HEIGHT_PT = 841.89;

/** The grid is centred on the sheet; what is left over holds the crop marks.
 *  A4 leaves 55.04 pt either side and 38.19 pt above and below. */
export const SHEET_MARGIN_X = (A4_WIDTH_PT - CARD_COLUMNS * CARD_WIDTH_PT) / 2;
export const SHEET_MARGIN_Y = (A4_HEIGHT_PT - CARD_ROWS * CARD_HEIGHT_PT) / 2;

/** Cards abut, so one mark serves both sides of a cut. Hairline, 3 pt clear of
 *  the trim box so the blade never has to land on printed ink. */
const CROP_MARK_LENGTH = 10;
const CROP_MARK_GAP = 3;
const CROP_MARK_WIDTH = 0.25;

/** Quiet margin inside the trim box — nothing prints closer to a cut edge.
 *  7 pt is 2.5 mm, several times a guillotine's tolerance. */
const CARD_PADDING = 7;
const CARD_INNER_WIDTH = CARD_WIDTH_PT - 2 * CARD_PADDING;

/** ~26 mm square. Level M carries a 234-character URL in a 61-module symbol, so
 *  a module lands at ~0.43 mm; measured on a 300 dpi raster of the real sheet,
 *  jsQR reads every card back byte for byte. The payload is a URL a phone reads
 *  from 10 cm, not archival material, so it does not need the sheets' 1.2 mm. */
const QR_FIT = 74;
const QR_COLUMN_GAP = 8;

const LABEL_COLUMN_WIDTH = 46;

/**
 * Width of the details column, stated EXPLICITLY rather than as `'*'`.
 *
 * A star column in pdfmake is a minimum, not a maximum: when its content cannot
 * fit — a 60-character email address, a long organization name — it grows and
 * pushes its fixed-width sibling along, and since the card is drawn at an
 * absolute position with nothing to clip it, the QR then prints outside the trim
 * box and over the card in the next column. Measured, not theorised: ten heavy
 * cards rendered exactly that way. A stated width makes the details wrap inside
 * their own box instead, which is what the vertical budget below accounts for.
 */
const DETAILS_COLUMN_WIDTH = CARD_INNER_WIDTH - QR_FIT - QR_COLUMN_GAP;

/** Width of the VALUE cells, stated for the same reason and one level down: a
 *  `'*'` table column is measured against the page, not against the column it
 *  sits in, so a long organization name or email ran past the details column and
 *  under the QR (visible only in a render). An explicit number is what makes
 *  pdfmake wrap the text instead. `paddingRight` is already 4 pt in
 *  {@link CARD_TABLE_LAYOUT}, so it is taken off here. */
const VALUE_COLUMN_WIDTH = DETAILS_COLUMN_WIDTH - LABEL_COLUMN_WIDTH - 4;

/** Error correction for the card's QR. A URL is cheap to re-print if it fails
 *  to scan (the address is on the card in text), and level H would cost ~30 %
 *  more modules on a symbol that is already small. */
export const INVITATION_CARD_QR_ECC: NonNullable<QrSvgOptions['ecc']> = 'M';

const TITLE_SIZE = 8.5;
const LABEL_SIZE = 6.5;
const VALUE_SIZE = 7;
const URL_SIZE = 6;
/** The code is the one thing on this card somebody has to read across a desk
 *  and type. It gets the biggest type on the card, and its own line. */
const CODE_SIZE = 12;


/** Like `COMPACT_TABLE_LAYOUT` but tighter still: at 0.5 pt the seven detail
 *  rows of a fully loaded card give back 7 pt, which is most of what a long
 *  organization name and a long email cost when they wrap. See the PAGE BUDGET
 *  note above `buildInvitationCard`. */
const CARD_TABLE_LAYOUT: TableLayout = {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    // 0.5 → 0.25 in round 10: the D20 sentence costs the card one 6 pt line, and
    // on the heaviest card — where the details column runs BELOW the QR — the
    // seven rows give a quarter point each back for it.
    paddingTop: () => 0.25,
    paddingBottom: () => 0.25,
    paddingLeft: () => 0,
    paddingRight: () => 4,
};

export const INVITATION_SHEET_TITLE = 'Catalog.ID Invitation Cards';

/**
 * What an invitation offers (registration-restructure §14.3) — the card's title
 * and which rows it prints follow from it.
 *
 * `delegate` is the §13.5 card and is the default, so every existing caller and
 * every printed sheet is unchanged. The other two are the generalised
 * invitations a voucher holder mints anonymously on the storefront: they have no
 * organization and no roles, because there is no organization behind them.
 */
export type InvitationCardKind = 'delegate' | 'personal' | 'organization';

/** Title line per kind. Each says plainly what the holder is being offered —
 *  somebody handed this card has to know whether it makes them a member of a
 *  company or gives them an identity of their own. */
export const INVITATION_CARD_TITLES: Record<InvitationCardKind, string> = {
    delegate: 'Catalog.ID Delegate Invitation',
    personal: 'Catalog.ID Registration Invitation',
    organization: 'Catalog.ID Organization Invitation',
};

/** One line, printed under the address, that says what to do with the card. */
export const INVITATION_CARD_INSTRUCTION =
    'Scan the QR, or open the address above and enter the code.';

/** Group a bits figure the way a card prints it: thin spaces every three
 *  digits, because a card is read across a desk. NBSP rather than a comma or a
 *  dot — neither reads the same way in every country this is printed in. */
export function formatBits(bits: number): string {
    return String(Math.round(bits)).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
}

/**
 * D20, in one sentence, on the card itself: an invitation code is money-bearing
 * for its window, and whoever holds the card can release its bits without
 * registering anything. The CID-side restriction stops the registration, not
 * the money — so the card has to say so.
 *
 * It does NOT repeat the amount: that is on the title row in 7 pt bold, and the
 * first cut — "This card is worth 10 000 000 bits to whoever holds it, until
 * 2026-11-13." — ran to 73 characters, which wraps to TWO lines at 6 pt in the
 * 228.6 pt card and cost the heavy card its quiet zone (300 dpi raster,
 * 2026-09-14). Keep this under ~70 characters at the widest date.
 */
export function bitsCarrierSentence(until: Date): string {
    return `This card's bits belong to whoever holds it, until ${formatCardDate(until)}.`;
}

// ── data ────────────────────────────────────────────────────────────────────

export interface InvitationCardData {
    /** The invitation code in DISPLAY form, `INV-XXXX-XXXX-XXXX-XXXX`. Printed
     *  in large type AND carried by the QR inside {@link url} — the two are the
     *  same code, so a card whose QR will not scan is still fully usable. */
    code: string;
    /** What this invitation offers (§14.3). Defaults to `delegate`, which is the
     *  §13.5 card exactly as it was. */
    kind?: InvitationCardKind;
    /** The organization's name, when the pane knows it. Delegate cards only. */
    organizationName?: string;
    /** Delegate cards only — there is no organization behind the other two. */
    orgCid?: string;
    /** Roles the invitation grants, in the order the inviter chose. Delegate
     *  cards only. */
    roles?: string[];
    /** The domain an ORGANIZATION invitation fixes. Printed in plaintext, like
     *  the fixed email and phone — a domain is public, and the founder has to
     *  read it off the card and type it. */
    domain?: string;
    /** The fixed email in PLAINTEXT — printed only when the invitation fixes
     *  one. The node holds nothing but its salted hash (§13.5). */
    email?: string;
    /** The fixed phone in plaintext, same rule. */
    phone?: string;
    /** When the invitation stops being redeemable (default 30 days out). */
    expiresAt: Date;
    /** Bits the invitation's lock carries (registration-wallets D20). Present ⇒
     *  a Bits row and {@link bitsWorthSentence} print; absent ⇒ neither does,
     *  which is every card printed before round 10. */
    bits?: number | null;
    /** When the BITS stop being claimable — the lock condition's `notAfter`,
     *  which is the invitation's expiry plus 30 days (D28). Defaults to
     *  {@link expiresAt}; the sentence must never promise the shorter of the
     *  two clocks. */
    bitsExpireAt?: Date;
    /** The entry address the QR carries — `activationUrlFor()` in
     *  `auth/delegate-activation-ref.ts`, built on the node's announced site or
     *  the dashboard helper origin, never on a bare `location.origin` (§11).
     *  It ends in `#code=…`, so it is the code again. */
    url: string;
    /** The same address WITHOUT the code, printed as the typeable line. Somebody
     *  transcribing by hand types this and then the code; the fragment would only
     *  be a second, harder copy of what is already in 12 pt above. */
    siteUrl?: string;
}

/**
 * The title line, with the bits figure right-aligned on the SAME row when the
 * card carries money (D20).
 *
 * Measured, not chosen: as a row of its own in the details table the figure cost
 * ~9 pt, and the heaviest card — a 53-character organization name, four roles, a
 * 62-character email and a phone, all wrapping — then ended 0.9 pt from the cut,
 * inside the 7 pt quiet zone (300 dpi raster, 2026-09-14). The title line has
 * ~80 pt of unused width on every card and no such pressure, so the figure moved
 * there and the vertical budget went back to what it was. It reads as what it is:
 * the card's value, beside the card's name.
 */
function cardTitleRow(title: string, bits: number | null): Content {
    if (bits === null) {
        return { text: title, fontSize: TITLE_SIZE, bold: true, margin: [0, 0, 0, 2] };
    }
    return {
        columns: [
            { text: title, fontSize: TITLE_SIZE, bold: true },
            {
                text: `${formatBits(bits)} bits`,
                fontSize: VALUE_SIZE, bold: true, alignment: 'right', width: 'auto',
            },
        ],
        columnGap: 4,
        margin: [0, 0, 0, 2],
    };
}

/** "YYYY-MM-DD", local time — the same clock the kit's version stamp uses. */
function formatCardDate(d: Date): string {
    const p = (n: number): string => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ── one card ────────────────────────────────────────────────────────────────

/**
 * The content of ONE card, sized for {@link CARD_INNER_WIDTH}. Laid out as a
 * title, then the details beside the QR, then the address across the full
 * width — the address is the long line, so it gets the whole card to wrap in
 * rather than the ~140 pt the left column could offer.
 *
 * PAGE BUDGET. There is no page break to absorb a card that grows: cards abut
 * on the sheet, so anything past 153.1 pt prints on top of the card below.
 * Measured on a 300 dpi raster (ink ends, out of 153.1 pt), re-measured for M18
 * and again for M19:
 *   sample sheet (name, ≤ two roles, one fixed value)          128.6 pt
 *   heavy  (53-char name, four roles, 62-char email + phone,
 *           long host — the name and the email both wrap)      142.6 pt
 *   M19 organization card (60-char domain + 66-char email,
 *           both wrapping, plus a phone)                       133.4 pt
 * The generalised kinds are LIGHTER than a loaded delegate card — no
 * organization name, no CID, no roles — so the M18 heavy case stays the
 * binding one; all five sample cards clear the cut by ≥ 19.7 pt with ≥ 7.2 pt
 * on every side, and all five QRs decode back to their printed codes.
 * So the heaviest realistic card still clears the cut by 10.5 pt, with ≥ 7.2 pt
 * on every side, and all ten of its QRs decode from that raster back to their
 * printed codes. Re-render `dev/render-booklet.ts <dir> --png` and MEASURE the
 * sheet after touching any size here — nothing in the unit tests can see a card
 * that grew into the one below it.
 *
 * Round 10 (2026-09-14) added the bits figure and the D20 sentence, and paid
 * for the sentence's line by halving the details table's row padding (0.5 →
 * 0.25) and taking a point off three block margins. Re-measured on a 300 dpi
 * raster of the sample sheet, ink end out of 153.1 pt:
 *   heavy delegate card + 10 000 000 bits + the sentence           145.9 pt
 *   organization card, 60-char domain + 66-char email + bits       137.3 pt
 *   the five pre-round-10 sample cards                     126.9 – 129.1 pt
 * so the heaviest card still clears the cut by 7.2 pt and keeps its quiet zone
 * on every side — but it is now the BINDING case, with nothing to spare. Two
 * earlier cuts are recorded because each ended inside the quiet zone: the bits
 * as a row in the details TABLE (0.9 pt clear — see `cardTitleRow` for where
 * the figure went instead), and a sentence that named the amount, which ran to
 * 73 characters and wrapped to two lines (see `bitsCarrierSentence`).
 *
 * The M18 measurement is what found the column widths below: with `'*'` on the
 * details column and its value cells, a long organization name or email pushed
 * the QR clean out of the trim box and printed it over the next card. Nothing
 * clips a card — it is drawn at an absolute position — so only a render shows
 * it. If you make a column elastic again, re-run the heavy case.
 */
export function buildInvitationCard(data: InvitationCardData): Content[] {
    const kind = data.kind ?? 'delegate';
    const rows: TableCell[][] = [];
    if (kind === 'delegate') {
        if (data.organizationName) {
            rows.push([
                { text: 'Organization', fontSize: LABEL_SIZE },
                { text: data.organizationName, fontSize: VALUE_SIZE, bold: true },
            ]);
            rows.push([{ text: 'CID', fontSize: LABEL_SIZE }, { text: data.orgCid ?? '', fontSize: VALUE_SIZE }]);
        } else {
            rows.push([
                { text: 'Organization', fontSize: LABEL_SIZE },
                { text: data.orgCid ?? '', fontSize: VALUE_SIZE, bold: true },
            ]);
        }
        const roles = data.roles ?? [];
        rows.push([
            { text: roles.length > 1 ? 'Roles' : 'Role', fontSize: LABEL_SIZE },
            { text: roles.join(', '), fontSize: VALUE_SIZE },
        ]);
    } else {
        // No organization and no roles: nobody is being made a member of
        // anything. What this card offers is a registration, so it says which.
        rows.push([
            { text: 'For', fontSize: LABEL_SIZE },
            {
                text: kind === 'personal' ? 'A personal Catalog.ID' : 'An organization',
                fontSize: VALUE_SIZE, bold: true,
            },
        ]);
        if (data.domain) {
            rows.push([
                { text: 'Domain (fixed)', fontSize: LABEL_SIZE },
                { text: data.domain, fontSize: VALUE_SIZE },
            ]);
        }
    }
    // "(fixed)" is the whole point of printing them: the invitee must verify
    // exactly these, and the validator refuses to certify anything else.
    if (data.email) {
        rows.push([{ text: 'Email (fixed)', fontSize: LABEL_SIZE }, { text: data.email, fontSize: VALUE_SIZE }]);
    }
    if (data.phone) {
        rows.push([{ text: 'Phone (fixed)', fontSize: LABEL_SIZE }, { text: data.phone, fontSize: VALUE_SIZE }]);
    }
    rows.push([
        { text: 'Expires', fontSize: LABEL_SIZE },
        { text: formatCardDate(data.expiresAt), fontSize: VALUE_SIZE },
    ]);
    const bits = data.bits ?? null;

    return [
        cardTitleRow(INVITATION_CARD_TITLES[kind], bits),
        // The code gets the card's FULL WIDTH and its own line. In the left
        // column beside the QR it wrapped after the third group, which is the
        // one thing on this card that must never be hard to read back.
        { text: data.code, fontSize: CODE_SIZE, bold: true, margin: [0, 0, 0, 3] },
        {
            columns: [
                {
                    width: DETAILS_COLUMN_WIDTH,
                    layout: CARD_TABLE_LAYOUT,
                    table: { widths: [LABEL_COLUMN_WIDTH, VALUE_COLUMN_WIDTH], body: rows },
                },
                {
                    width: QR_FIT,
                    svg: qrToSvg(encodeUtf8(data.url), { ecc: INVITATION_CARD_QR_ECC }),
                    fit: [QR_FIT, QR_FIT],
                },
            ],
            columnGap: QR_COLUMN_GAP,
            margin: [0, 0, 0, 2],
        },
        { text: data.siteUrl ?? data.url, fontSize: URL_SIZE, margin: [0, 0, 0, 1] },
        { text: INVITATION_CARD_INSTRUCTION, fontSize: URL_SIZE },
        ...(bits !== null
            ? [{
                text: bitsCarrierSentence(data.bitsExpireAt ?? data.expiresAt),
                fontSize: URL_SIZE, bold: true, margin: [0, 1, 0, 0] as [number, number, number, number],
            }]
            : []),
    ];
}

/** Top-left corner of card `index` on its sheet, in page points. */
export function invitationCardOrigin(index: number): { x: number; y: number } {
    const column = index % CARD_COLUMNS;
    const row = Math.floor(index / CARD_COLUMNS);
    return {
        x: SHEET_MARGIN_X + column * CARD_WIDTH_PT,
        y: SHEET_MARGIN_Y + row * CARD_HEIGHT_PT,
    };
}

/** Card content placed at `origin`. The outer node sits on the TRIM box; the
 *  padding is the inner column's margin, so the grid maths never has to know
 *  about it. Exported because the VOUCHER card (registration-wallets §7) is a
 *  different card on the SAME pipeline: one trim box, one grid, one set of crop
 *  marks, measured once. */
export function positionCardContent(
    content: Content[], origin: { x: number; y: number },
): Content {
    return {
        absolutePosition: origin,
        columns: [{
            width: CARD_WIDTH_PT,
            stack: content,
            margin: [CARD_PADDING, CARD_PADDING, CARD_PADDING, CARD_PADDING],
        }],
    };
}

// ── the A4 sheet ────────────────────────────────────────────────────────────

interface CropLine { type: 'line'; x1: number; y1: number; x2: number; y2: number; lineWidth: number }

/**
 * Crop marks for a sheet holding `count` cards: one pair of ticks at every cut
 * line the sheet actually needs, outside the trim box on both ends. A sheet of
 * three cards is marked for two rows, not five — the blank rows below carry no
 * cuts.
 *
 * They live in the page BACKGROUND rather than the content, which is also what
 * gives the document a background at all: `withLetterhead()` fills one in only
 * when the document brought none, and the letterhead must never land on a sheet
 * that is about to be cut into ten pieces.
 */
export function invitationCropMarks(count: number): Content {
    const columns = Math.min(Math.max(count, 1), CARD_COLUMNS);
    const rows = Math.ceil(Math.max(count, 1) / CARD_COLUMNS);
    const right = SHEET_MARGIN_X + columns * CARD_WIDTH_PT;
    const bottom = SHEET_MARGIN_Y + rows * CARD_HEIGHT_PT;
    const line = (x1: number, y1: number, x2: number, y2: number): CropLine =>
        ({ type: 'line', x1, y1, x2, y2, lineWidth: CROP_MARK_WIDTH });

    const canvas: CropLine[] = [];
    for (let c = 0; c <= columns; c++) {
        const x = SHEET_MARGIN_X + c * CARD_WIDTH_PT;
        canvas.push(line(x, SHEET_MARGIN_Y - CROP_MARK_GAP - CROP_MARK_LENGTH, x, SHEET_MARGIN_Y - CROP_MARK_GAP));
        canvas.push(line(x, bottom + CROP_MARK_GAP, x, bottom + CROP_MARK_GAP + CROP_MARK_LENGTH));
    }
    for (let r = 0; r <= rows; r++) {
        const y = SHEET_MARGIN_Y + r * CARD_HEIGHT_PT;
        canvas.push(line(SHEET_MARGIN_X - CROP_MARK_GAP - CROP_MARK_LENGTH, y, SHEET_MARGIN_X - CROP_MARK_GAP, y));
        canvas.push(line(right + CROP_MARK_GAP, y, right + CROP_MARK_GAP + CROP_MARK_LENGTH, y));
    }
    return { canvas, absolutePosition: { x: 0, y: 0 } };
}

/**
 * The print job: up to ten cards per A4 sheet, 2 × 5, cut apart along the crop
 * marks. Pass it to `printPdf()` — it carries its own background, so no
 * letterhead is stamped on it.
 */
export function buildInvitationCardSheet(cards: InvitationCardData[]): TDocumentDefinitions {
    return buildCardSheetDocument(cards, buildInvitationCard, INVITATION_SHEET_TITLE,
        'There are no invitation cards to print');
}

/**
 * The generic ten-up sheet: up to ten cards per A4, 2 × 5, cut apart along the
 * crop marks, carrying its own background so no letterhead is stamped on it.
 * `build` turns one card's data into its content — the invitation card above,
 * the voucher card in `voucher-card.ts`.
 */
export function buildCardSheetDocument<T>(
    cards: readonly T[], build: (card: T) => Content[], title: string, emptyMessage: string,
): TDocumentDefinitions {
    if (cards.length === 0) throw new Error(emptyMessage);
    const sheets: T[][] = [];
    for (let i = 0; i < cards.length; i += CARDS_PER_SHEET) {
        sheets.push(cards.slice(i, i + CARDS_PER_SHEET));
    }
    const counts = sheets.map(sheet => sheet.length);
    return {
        pageSize: 'A4',
        pageMargins: [0, 0, 0, 0],
        info: { title },
        background: (currentPage: number) => invitationCropMarks(counts[currentPage - 1] ?? 0),
        content: sheets.map((sheet, sheetIndex) => ({
            stack: sheet.map((card, i) => positionCardContent(build(card), invitationCardOrigin(i))),
            ...(sheetIndex === 0 ? {} : { pageBreak: 'before' as const }),
        })),
    };
}

// ── one card as its own page (the PNG export) ───────────────────────────────

/**
 * A one-page document whose page IS the card: no margins, no crop marks, a
 * white ground. Rasterising this page yields exactly the card and nothing
 * around it.
 */
export function buildInvitationCardDocument(card: InvitationCardData): TDocumentDefinitions {
    return buildCardDocument(buildInvitationCard(card), INVITATION_SHEET_TITLE);
}

/** The generic one-card page, for the PNG export of any card on this pipeline. */
export function buildCardDocument(content: Content[], title: string): TDocumentDefinitions {
    return {
        pageSize: { width: CARD_WIDTH_PT, height: CARD_HEIGHT_PT },
        pageMargins: [0, 0, 0, 0],
        info: { title },
        // A PDF page is transparent where nothing is drawn, and a transparent
        // canvas reads back as BLACK — the same trap the sheet scanners hit
        // (§11). Painting the ground here means the PNG is right whatever the
        // rasteriser does about backgrounds. It also keeps `withLetterhead()`
        // away, should this document ever reach printPdf.
        background: (_currentPage: number, pageSize: { width: number; height: number }): Content => ({
            canvas: [{
                type: 'rect', x: 0, y: 0, w: pageSize.width, h: pageSize.height, color: '#ffffff',
            }],
            absolutePosition: { x: 0, y: 0 },
        }),
        content: [positionCardContent(content, { x: 0, y: 0 })],
    };
}
